import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui';
import type { AppRoleOption } from '@/api/console';

export interface RolePickerPopoverProps {
    appName: string;
    roleKey: string;
    options: AppRoleOption[];
    loading: boolean;
    alreadyLinked: string[];
    onPick: (appRole: string) => void;
    onClose: () => void;
}

/**
 * EAA-1817: "+ vai trò" — picks one of `{app}`'s CATALOG roles to link to a
 * standard role. Options come only from `fetchAppRoles` (already-synced
 * catalog rows); there is deliberately no free-text input, so an admin can
 * never link to an `app_role` that doesn't exist in the app's catalog — the
 * exact mistake `is_stale`/paused rows exist to detect after the fact.
 *
 * A lightweight popover rather than a modal: it is scoped to one table row,
 * needs no focus trap, and Escape / an outside click both simply close it
 * (focus returns to the "+ vai trò" trigger button that opened it).
 */
export function RolePickerPopover({
    appName,
    roleKey,
    options,
    loading,
    alreadyLinked,
    onPick,
    onClose,
}: RolePickerPopoverProps) {
    const { t } = useTranslation();
    const [query, setQuery] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const linkedSet = useMemo(() => new Set(alreadyLinked), [alreadyLinked]);

    useEffect(() => {
        function onPointerDown(event: MouseEvent) {
            if (!containerRef.current) return;
            if (!containerRef.current.contains(event.target as Node)) onClose();
        }
        function onKeyDown(event: KeyboardEvent) {
            if (event.key === 'Escape') onClose();
        }
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [onClose]);

    const normalizedQuery = query.trim().toLowerCase();
    const filtered = options.filter(
        (option) =>
            !normalizedQuery ||
            `${option.key} ${option.name}`.toLowerCase().includes(normalizedQuery)
    );

    return (
        <div
            ref={containerRef}
            className="rc-role-picker"
            role="dialog"
            aria-label={t('console.roleCoverage.rolePicker.dialogAriaLabel', { appName, roleKey })}
        >
            <label className="rc-role-picker-search">
                <span className="portal-sr-only">
                    {t('console.roleCoverage.rolePicker.searchSrLabel', { appName })}
                </span>
                {/* EAA-1817: raw <input> → kit Input; `autoFocus` is a plain HTML
                    attribute forwarded straight through to the underlying antd
                    `<input>`, so the "focus the search box on open" behavior this
                    popover relies on (see the file docstring) is unaffected. */}
                <Input
                    autoFocus
                    placeholder={t('console.roleCoverage.rolePicker.searchPlaceholder', {
                        appName,
                    })}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                />
            </label>

            {loading && (
                <p className="portal-admin-hint" role="status">
                    {t('console.roleCoverage.rolePicker.loadingCatalog')}
                </p>
            )}

            {!loading && filtered.length === 0 && (
                <p className="rc-role-picker-empty">
                    {t('console.roleCoverage.rolePicker.noMatch')}
                </p>
            )}

            {!loading && filtered.length > 0 && (
                <ul className="rc-role-picker-list">
                    {filtered.map((option) => {
                        const alreadyAdded = linkedSet.has(option.key);
                        return (
                            <li key={option.key}>
                                <button
                                    type="button"
                                    className="rc-role-picker-option"
                                    disabled={alreadyAdded}
                                    onClick={() => onPick(option.key)}
                                >
                                    <span className="rc-role-picker-option-key">{option.key}</span>
                                    <span className="rc-role-picker-option-name">
                                        {option.name}
                                    </span>
                                    {alreadyAdded && (
                                        <span className="rc-role-picker-option-added">
                                            {t('console.roleCoverage.rolePicker.alreadyAdded')}
                                        </span>
                                    )}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}

            <p className="rc-role-picker-hint">
                {t('console.roleCoverage.rolePicker.hint')}
            </p>
        </div>
    );
}

export default RolePickerPopover;
